import {UpgradeTypes, SiteUpgrade} from './definitions'
import {
  NativePanelKeys,
  PanelConfigurationType,
} from './sdk/editor/component-panels'
/**
 * This is the interface for the object (or resolved object) returned from the `getAppManifest` function. [Learn more](../../articles/manifest-intro.md).
 */
export interface AppManifest {
  /**
   * Map of stage data by controller type.
   */
  controllersStageData?: ControllerStageDataManifest
  routers?: RoutersManifest
  pages?: PagesManifest
  appDescriptor?: AppDescriptor
  exports?: ExportsManifest
}

export default AppManifest

/**
 * A map of stage data per controller state. The initial state for all controllers is `default`,
 * and the data for every other state is merged with the `default` state. [Learn more](../../articles/controller-stage-data-manifest.md).
 */
export type ControllerManifest = {
  default: ControllerStateStageData
  [state: string]: ControllerStateStageData
}

/**
 * A map of stage data per widget state. The initial state for all widgets is `default`,
 * and the data for every other state is merged with the `default` state. [Learn more](../../articles/controller-stage-data-manifest.md).
 */
export type WidgetManifest = {
  default: WidgetStateStageData
  [state: string]: WidgetStateStageData
}

export interface ControllerStageDataManifest {
  [id: string]: ControllerManifest | WidgetManifest
}

export enum MemberKind {
  MEMBER = 'member',
  FUNCTION = 'function',
}

/**
 * MemberKind represented as strings union
 */
export type MemberKindStrings = 'member' | 'function'

export interface MemberDefinition {
  kind: 'member'
  description: string
}

export interface MethodDefinition {
  kind: 'function'
  description: string
  /**
   * List of parameters that the method accepts.
   */
  params?: [
    {
      /**
       * Parameter name.
       */
      name: string
      /**
       * Parameter description.
       */
      description: string
    },
  ]
}

/**
 * Controller public API, used to display intellisense in the Velo (former Corvid) code editor.
 */
export interface ControllerExportsManifest {
  inherits?: {
    /**
     * Map of other defined types that this controller type inherits from.
     */
    [controllerType: string]: boolean
  }
  /**
   * Map of events that the controller exposes.
   */
  eventHandlers?: {
    /**
     * The event handler method name that is used to register for the event.
     *
     * @example 'onWidgetLoaded'
     */
    [eventHandlerName: string]: {
      /**
       * The event type that is fired
       *
       * @example 'widgetLoaded'
       */
      type: string
      /**
       * Event description
       */
      description: string
    }
  }
  /**
   * Map of members or methods that the controller exposes.
   */
  members?: {
    /**
     * Name of member or method.
     */
    [propertyName: string]: MemberDefinition | MethodDefinition
  }
}

export interface ExportsManifest {
  /**
   * Map of public API exports by controller type, used to display intellisense in the Velo (former Corvid) code editor.
   */
  [controllerType: string]: ControllerExportsManifest
}

/**
 * Learn more about the GFPP in [this article](../../articles/gfpp.md).
 */
export interface GfppControllerConfig {
  /**
   * Define the first main textual action.
   */
  mainAction1?:
    | MainActionDefinition
    | GfppWithBehavior<'CUSTOM_ACTION', MainActionDefinition>
  /**
   * Define the second textual action.
   */
  mainAction2?:
    | MainActionDefinition
    | GfppWithBehavior<'CUSTOM_ACTION', MainActionDefinition>
}

/**
 * If you want to open the GFPP of a component that belongs to the same widget, you should declare a "target".
 * You should specify the target component, by its role, if the role is unique. If the role alone is not unique, you should also use the subRole to identify the target.
 */
export interface ComponentGfppTarget {
  /**
   * The component's role. If the component's role is unique, then the role is sufficient to identify the correct target.
   */
  role: string
  /**
   * The component's subRole. If the component's role is not unique, then the subRole should be used in combination with the role to identify the correct target.
   */
  subRole?: string
}

/**
 * If you want to open the GFPP of an App Widget that is part of the same widget as your component, you should declare a "target".
 * You should specify the target component, by its controllerType.
 */
export interface ControllerGfppTarget {
  /**
   * The app widget's controllerType. This controllerType is unique per widget, and therefore can be used to identify the single GFPP target.
   */
  controllerType: string
}

export interface GfppTargetWithActionLabel {
  /**
   * Optional. The label shown on the GFPP button and on the right-click menu option. This should be used if you are mapping the origin component`s textual action to the target component's icon button action.
   */
  label?: string
}

export interface GfppTargetWithActionType extends GfppTargetWithActionLabel {
  /**
   * Optional. Clicking this GFPP will open the specified actionType of the target. E.g. if the actionType is 'settings', this gfpp button will be overridden with the target's 'settings' action.
   * Note: If no actionType or actionId is specified, then this GFPP button will be overridden by the equivalent target's action. I.e. if the target is declared for a component's mainAction1, then it will be overridden by the target's mainAction1.
   */
  actionType?: ActionTypeStrings
}

/**
 * Declaration for a connected component GFPP Target with action id.
 */
export interface GfppTargetWithActionId extends GfppTargetWithActionLabel {
  /**
   * Optional. Clicking this GFPP will open the specified actionId of the target. E.g. if the actionId is 'custom_settings', this gfpp button will be overridden with the target's 'custom_settings' action.
   * Note: If no actionType or actionId is specified, then this GFPP button will be overridden by the equivalent target's action. I.e. if the target is declared for a component's mainAction1, then it will be overridden by the target's mainAction1.
   */
  actionId?: string
}

/**
 * If you would like to override your GFPP menu with the GFPP of a related component, you should declare a "target".
 * If the "target" is an app widget, you should declare it by its controllerType. If the "target" has a unique role, you should declare it via its role. Otherwise it should be declared via its role and subRole.
 */
export interface GfppTarget extends GfppTargetWithActionLabel {
  target: ControllerGfppTarget | ComponentGfppTarget
}

/**
 * If you would like to override a specific GFPP action with a GFPP action from a related component, you should declare a "target".
 * If the "target" is an app widget, you should declare it by its controllerType. If the "target" has a unique role, you should declare it via its role. Otherwise it should be declared via its role and subRole.
 * If you want to specify which action of the "target" the action maps to, you can declare it according to its "actionId" or "actionType".
 * If you do not specify an action to map to, the action will be mapped to the equivalent action in the "target".
 * E.g. If you declare the "target" as part of a component's "mainAction1", by default "mainAction1" will be overridden by the "target"'s "mainAction1".
 */
export interface GfppTargetWithAction {
  target: (ControllerGfppTarget | ComponentGfppTarget) &
    (GfppTargetWithActionId | GfppTargetWithActionType)
}

/**
 * Learn more about the GFPP in [this article](../../articles/gfpp.md).
 */
export interface GfppDesktopConfig {
  /**
   * Override the first main textual action.
   */
  mainAction1?:
    | GfppMainActionButtonConfigStrings
    | MainActionDefinition
    | GfppAdvancedButton
    | GfppTargetWithAction
  /**
   * Override the second textual action.
   */
  mainAction2?:
    | GfppMainActionButtonConfigStrings
    | MainActionDefinition
    | GfppAdvancedButton
    | GfppTargetWithAction
  /**
   * Override the icon buttons.
   */
  iconButtons: {
    settings?:
      | GfppButtonConfigStrings
      | ActionDefinition
      | GfppBasicButton<ActionDefinition>
      | GfppTarget
    add?:
      | GfppButtonConfigStrings
      | ActionDefinition
      | GfppBasicButton<ActionDefinition>
      | GfppTarget
    design?:
      | GfppButtonConfigStrings
      | ActionDefinition
      | GfppBasicButton<ActionDefinition>
      | GfppTarget
    crop?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    filters?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    animation?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    link?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    textLink?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    stretch?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    presets?: GfppBasicButton | GfppTarget
    layout?:
      | GfppButtonConfigStrings
      | ActionDefinition
      | GfppBasicButton<ActionDefinition>
      | GfppTarget
    upgrade?:
      | GfppButtonConfigStrings
      | SiteUpgradeActionDefinition
      | ActionDefinition
      | GfppUpgradeButton<ActionDefinition>
      | GfppTarget
    connect?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    pinMode?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    focalPoint?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    widgetPlugins?: GfppButtonConfigStrings
    customConnect?:
      | ActionDefinition
      | GfppWithBehavior<'CUSTOM_ACTION', ActionDefinition>
      | GfppTarget
  }
  /**
   * The help ID for the connected component.
   * If you want to open the help panel of a related component, you should specify the "caller" according to its controllerType, or role and subRole.
   */
  helpId?: string | GfppTarget
  /**
   * Learn more about Widget Design in [this article](../../articles/widget-design-manifest.md).
   */
  widgetDesign?: WidgetDesignManifest
  widgetPresets?: WidgetPresetsManifest
}

/**
 * `SITE_UPGRADE` action definition for overridden "upgrade" GFPP icon button
 */
export type SiteUpgradeActionDefinition = SiteUpgrade

/**
 * Learn more about the GFPP in [this article](../../articles/gfpp.md).
 */
export interface GfppMobileConfig {
  /**
   * Override the first main textual action.
   */
  mainAction1?:
    | GfppButtonConfigStrings
    | MainActionDefinition
    | GfppBasicButton<MainActionDefinition>
    | GfppTargetWithAction
  /**
   * Override the second textual action.
   */
  mainAction2?:
    | GfppButtonConfigStrings
    | MainActionDefinition
    | GfppBasicButton<MainActionDefinition>
    | GfppTargetWithAction
  /**
   * Override the icon buttons.
   */
  iconButtons: {
    textSize?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    settings?:
      | GfppButtonConfigStrings
      | ActionDefinition
      | GfppBasicButton<ActionDefinition>
      | GfppTarget
    design?:
      | GfppButtonConfigStrings
      | ActionDefinition
      | GfppBasicButton<ActionDefinition>
      | GfppTarget
    crop?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    animation?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    focalPoint?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    presets?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    layout?:
      | GfppButtonConfigStrings
      | ActionDefinition
      | GfppBasicButton<ActionDefinition>
      | GfppTarget
    hide?: GfppButtonConfigStrings | GfppBasicButton | GfppTarget
    upgrade?:
      | ActionDefinition
      | GfppWithBehavior<'CUSTOM_ACTION', ActionDefinition>
      | GfppTarget
  }
  /**
   * The help ID for the connected component.
   * If you want to open the help panel of a related component, you should specify the "caller" according to its controllerType, or role and subRole.
   */
  helpId?: string | GfppTarget
}

export interface ComponentClosedBehavior {
  /**
   * Defines whether the component can be selected when it's contained in a closed widget (default is *true*).
   */
  selectable?: boolean

  /**
   * Defined whether a component in a closed widget is hidden from the hierarchy in the layers panel (default is *false*).
   * Note: a component is hidden from the hierarchy only if it's also not selectable.
   */
  hideFromHierarchy?: boolean
}

/**
 * Defines the edit options of the component, for example, you can limit text editing to style, so that the user can't change the text itself.
 */
export type TextComponentDataEditOptions = 'FULL' | 'TEXT_STYLE_ONLY'

export interface ComponentStageBehavior {
  /**
   * Defines whether the component can be duplicated (default is `true`).
   * When set to `false` for the component:
   * - Cut/Copy menu items don't appear in the component's right-click menu.
   * - Cut/Copy keyboard shortcuts don't work for this component.
   * - Cut/Copy actions are greyed out in the toolbar.
   */
  duplicatable?: boolean

  /**
   * Defines whether the component can be rotated (default is `true`).
   * When set to `false` for the component:
   * - The rotate handle does not appear when it's selected.
   * - The rotation degrees input in the toolbar is greyed out.
   */
  rotatable?: boolean

  /**
   * Specifies whether the component can be pinned to an area of the screen or unpinned from it (default is `true`).
   */
  pinnable?: boolean
  /**
   * Specifies whether showOnAllPages can be toggled for this component (default is `true`).
   */
  toggleShowOnAllPagesEnabled?: boolean

  /**
   * Defines whether the component can be resized (default is `true`).
   */
  resizable?: boolean

  /**
   * Defines whether the component can be re-parented, meaning can be grouped/un-grouped or attached/detached from parent (default is `true`).
   */
  canReparent?: boolean

  /**
   * Defines the edit options of the component, for example, you can limit text editing to style, so that the user can't change the text itself.
   * Currently used only by text component.
   * optional dataEditOptions values for text component are: 'FULL' || 'TEXT_STYLE_ONLY'
   * (default is 'FULL')
   */
  dataEditOptions?: TextComponentDataEditOptions

  /**
   * Defines the options for removing a closed referring (byRef) component.
   * Used only on referring components.
   * Note that the component is not actually removed, but collapsed. To the user, it appears to be removed.
   * If set to *true* prevents the removal of the component.
   * Default value is *false*.
   */
  preventHide?: boolean

  /**
   * An object that defines whether the element is essential, and provides tooltip and notification text upon user deletion of an essential element.
   * If an essential element was deleted, a warning banner is displayed in the component panel. The app can prevent the notification by setting the [displayEssentialBanner](../../Editor/Editor.md#opencomponentpanel) property to *false*.
   */
  essential?: {
    /**
     * Defines whether the element is essential (default is *false*).
     * When set to *true*:
     * - In closed mode, the element can't be deleted (hidden), even if preventHide = *false*.
     * - In open mode, the user can delete the element but gets a warning notification and additional suggestions that they should re-add the element.
     */
    enabled: boolean

    /**
     * This text is used for the info tooltip displayed in addElementsPanel and mobileHiddenElementsPanel.
     * It is also used for the warning notification when deleting an element from an open widget.
     * If not supplied a default text is used.
     */
    text?: string
  }

  /**
   * Defines the connected component's behavior on stage in a closed widget. The behaviors here have no effect when the widget is open.
   */
  closed?: ComponentClosedBehavior
}

export interface WidgetStageBehavior extends ComponentStageBehavior {
  /**
   * Specifies whether the widget can be closed and wrapped by a component that loads its content from a remote location (default is `false`).
   * Note that this option is used by widgets that are built with AppBuilder and that are exposed to Site Assets in the public segment.
   */
  closable?: boolean

  /**
   * Specifies whether the widget can be removed (default is `true`).
   */
  removable?: boolean
}

export interface ConnectedComponentConfig {
  /**
   * Data influencing the GFPP and right-click menu of the connected component.
   */
  gfpp?: GfppConfig

  /**
   * Specifies the connected component's behaviors on stage.
   * Note: Stage behaviors can only be limited from the manifest, not extended.
   * E.g.: The "Facebook Like" component is not rotatable.
   * Setting `rotatable` to `true` for a connected "Facebook Like" component does not change this.
   */
  behavior?: ComponentStageBehavior

  /**
   * Data influencing the panel controls and control groups of the connected component.
   */
  panel?: ComponentPanelsConfig

  /**
   * Specifies the display name of the component once it's connected.
   * Displayed in the component label on hover in the Editor.
   */
  displayName?: string

  /**
   * If true (default = false) specifies that:
   * - a focus box is displayed in components that don't have one by default (such as buttons).
   * - the display name is displayed in the focus box of components that do have one by default (such as repeaters). Otherwise the default name, such as 'Repeater', is displayed.
   */
  showFocusBox?: boolean

  /**
   * Allows hiding an existing component's first time panel, all other properties are ignored.
   */
  firstTimeExperience?: {
    /**
     * Sets whether the panel is hidden, default is `false`* (panel is visible).
     */
    hidden: boolean
  }
}

/**
 * The stage data for controllers by type and state. Learn more in [this article](../../articles/controller-stage-data-manifest.md).
 * Learn more about controllers in [this article](../../articles/controllers.md)
 */
export interface ControllerStateStageData {
  /**
   * DEPRECATED - use GfppControllerConfig instead.
   * Config for the main textual for the controller.
   */
  mainAction?: {
    /**
     * The label for the main textual action for a controller type and state.
     */
    label: string
  }

  /**
   * Determines when the controller is visible.
   */
  visibility?: ControllerVisibilityStrings
  connections?: ControllerConnections

  /**
   * The URL of the controller type and state's icon image in the Editor.
   * Should be a 48px x 48px vector image.
   */
  icon?: string

  /**
   * Specifies whether `showOnAllPages` can be toggled for this controller (default is `true`)
   * Note: this property used to be called `enableShowOnAllPages`, which is deprecated.
   */
  toggleShowOnAllPagesEnabled?: boolean

  /**
   * Specifies whether the controller can be removed (default is `true`).
   */
  removable?: boolean

  /**
   * Specifies whether the controller can be duplicated (default is `true`).
   */
  duplicatable?: boolean

  /**
   * Triggers a *first time experience* panel that opens when the controller
   * is first selected, and gives a quick overview of its usage.
   */
  firstTimeExperience?: FirstTimeExperience

  /**
   * GFPP definition for visible controllers.
   */
  gfpp?: GfppControllerConfig

  /**
   * Specifies the controller's layout limitations,
   * allowing you to set the minimum and maximum width and height of the controller.
   * > **Note**: Wix Editor only
   */
  layoutLimits?: LayoutLimits
}

export interface WidgetViewState {
  title?: string
  gfpp?: GfppConfig
  initial?: boolean
  /**
   * Indicates whether this view state is hidden.
   * Can only be set to true when all view states are hidden by hideAllWidgetViewStates().
   * When true, this view state is not displayed in the editor.
   */
  hidden?: boolean
}

/**
 * The stage data for widgets by type and state. Learn more in [this article](../../articles/controller-stage-data-manifest.md).
 */
export interface WidgetStateStageData {
  connections?: ControllerConnections

  /**
   * Specifies the widget's behavior on stage.
   * Note: Stage behaviors can only be limited from the manifest, not extended.
   */
  behavior?: WidgetStageBehavior

  gfpp?: GfppConfig

  /**
   * Specifies the widget's display name.
   * Displayed in the component label on hover in the Editor, and when interacting with its children.
   */
  displayName?: string

  /**
   * Specifies the base for the Velo nickname that is generated when adding a widget to the stage.
   * Names for multiple instances of the same widget are the base and a sequential number.
   */
  nickname?: string

  /**
   * Triggers a 'first time experience' panel that opens when the controller
   * is first selected, and gives a quick overview of its usage.
   */
  firstTimeExperience?: FirstTimeExperience

  /**
   * Specifies the widget's layout limitations,
   * allowing you to set the minimum and maximum width and height of the widget.
   * > **Note**: Wix Editor only
   */
  layoutLimits?: LayoutLimits

  /**
   * Specifies the widget`s slots
   */
  slots?: SlotsManifest

  /**
   * Widget view states
   */
  viewStates?: Record<string, WidgetViewState>
}

/**
 * Map of slots IDs
 * @example
 * {
 *   slot1: { ... },
 *   slot2: { ... },
 * }
 */
export type SlotsManifest = {
  [slotId: string]: SlotManifest
}

export type SlotManifest = {
  /**
   * List of a slot interfaces
   * @example { interfaces: ["DEFAULT", "REVIEWS", "ENV"] }
   */
  interfaces: string[]

  /**
   * Defines a name of a slot to be displayed in the "Move plugin" panel
   */
  displayName?: string
}

export type FirstTimeExperience = {
  /**
   * Translated title for the panel.
   */
  contentTitle?: string

  /**
   * Translated content for the panel.
   */
  content?: string

  /**
   * Must be provided with linkText. Specifies the ID of the help article to open
   * when a user clicks the linkText text.
   */
  helpId?: string

  /**
   * Must be provided with helpId. Translated text for a link to open help panel.
   */
  linkText?: string

  /**
   * Background color for the panel head section. Default is '#B13DAC'.
   */
  bgColor?: string

  /**
   * Media URL for a gif displayed on the panel head section.
   */
  headerBg?: string

  /**
   * Media URL for a gif (retina version) displayed on the panel head section.
   */
  headerBgRetina?: string

  /**
   * Sets whether the panel is hidden. Default is `false` (panel is visible).
   */
  hidden: boolean
}

export type ControllerConnections = {
  /**
   * The stage data for each type of role connected to the controller of the specified type and state.
   */
  [role: string]: ConnectedComponentConfig | undefined

  /**
   * role type '*' is reserved and is used as the default when behavior for the specific role is not defined.
   */
  '*'?: ConnectedComponentConfig
}

export type GfppConfig = {
  /**
   * Override the component's GFPP in desktop view.
   */
  desktop?: GfppDesktopConfig | GfppTarget
  /**
   * Override the component's GFPP in mobile view.
   */
  mobile?: GfppMobileConfig | GfppTarget
}

export type ComponentPanelsConfig = {
  /**
   * Override the component's panel controls
   */
  [NativePanel in NativePanelKeys]?: PanelConfigurationType<NativePanel>
}

export type LayoutLimitConfig = {
  /**
   * Override the component's min-width.
   */
  minWidth?: number
  /**
   * Override the component's min-height.
   */
  minHeight?: number
  /**
   * Override the component's max-width.
   */
  maxWidth?: number
  /**
   * Override the component's max-height.
   */
  maxHeight?: number
}

export type LayoutLimits = {
  /**
   * Override the component's layout limits in desktop view.
   */
  desktop?: LayoutLimitConfig
  /**
   * Override the component's layout limits in mobile view.
   */
  mobile?: LayoutLimitConfig
}

/**
 * Configuration for Controller Visibility
 */
export const enum ControllerVisibility {
  /**
   * Controllers are visible in Developer Mode (default).
   */
  DEV = 'DEV',

  /**
   * Controllers are never visible.
   */
  NEVER = 'NEVER',
}

/**
 * Configuration for Controller Visibility represented as strings union
 */
export type ControllerVisibilityStrings = keyof typeof ControllerVisibility

/**
 @deprecated - use GfppBasicButton instead.
 */
export const enum GfppButtonConfig {
  /**
   * The GFPP button and right-click menu action are hidden.
   */
  HIDE = 'HIDE',

  /**
   * The GFPP button and right-click menu action are default behavior.
   */
  DEFAULT = 'DEFAULT',
}

/**
 * @deprecated - use GfppBasicButton instead.
 */
export type GfppButtonConfigStrings = keyof typeof GfppButtonConfig

/**
 @deprecated - use GfppAdvancedButton instead.
 */
export const enum GfppMainActionButtonConfig {
  /**
   * The GFPP button and right-click menu action are hidden.
   */
  HIDE = 'HIDE',

  /**
   * The GFPP button and right-click menu action have the default behavior.
   */
  DEFAULT = 'DEFAULT',

  /**
   * The GFPP button and right-click menu action are the default *first* action.
   */
  DEFAULT1 = 'DEFAULT1',

  /**
   * The GFPP button and right-click menu action are the default *secondary* action.
   */
  DEFAULT2 = 'DEFAULT2',
}

/**
 @deprecated - use GfppAdvancedButton instead.
 */
export type GfppMainActionButtonConfigStrings =
  keyof typeof GfppMainActionButtonConfig

/**
 * Defines any action overidden from the GFPP and the right-click menu.
 */
export interface ActionDefinition extends ActionPermissions {
  /**
   *  The action ID sent in the `componentGfppClicked` event sent to the Editor.
   */
  actionId: string
}

/**
 * GFPP's main action type, specifies a predefined GFPP used for the main action.
 */
export const enum ActionType {
  SETTINGS = 'settings',
  LAYOUT = 'layout',
  DESIGN = 'design',
}

/**
 * GFPP's main action type represented as strings union.
 */
export type ActionTypeStrings = 'settings' | 'layout' | 'design' | 'presets'

/**
 * Defines a textual main action for the component.
 */
export interface MainActionDefinition extends ActionDefinition {
  /**
   * The label shown on the GFPP button and on the right-click menu option.
   */
  label: string

  /**
   * Optional. Use an Editor-defined GFPP. Should be one of the displayed iconButtons (not in "*hide* mode).
   * In mobile make sure the actionType represents an iconButton that is displayed in mobile.
   * This action is not displayed in the right-click menu.
   *
   * Note: If you provide both actionId and actionType, the Viewer opens the actionType panel and notifies the app with the actionID.
   */
  actionType?: ActionTypeStrings
  /**
   * The actionName that will be reported to BI. This string should be untranslated so that it is consistent for all reported BI events.
   *
   * @deprecated The eventType will be reported to BI, and this is never translated. Therefore it is unnecessary to provide an actionName solely for BI purposes.
   */
  actionName?: string
}

/**
 * Supported GFPP behaviors:
 * - `HIDE`: The action is hidden.
 * - `DEFAULT`: If the action exists for the component, the button is shown and has the default behavior.
 * - `DEFAULT1`: If the action exists for the component, the button is shown with the default *first* behavior.
 * - `DEFAULT2`: If the action exists for the component, the button is shown with the default *secondary* behavior.
 * - `CUSTOM_ACTION`: Allows to specify a custom action for the button.
 * - `SITE_UPGRADE`: Applicable for the upgrade button only.
 * Use `DEFAULT1` and `DEFAULT2` to swap the actions for the two main action buttons and their associated right-click behaviors.
 */
export type GfppBehavior =
  | 'DEFAULT'
  | 'DEFAULT1'
  | 'DEFAULT2'
  | 'HIDE'
  | 'CUSTOM_ACTION'
  | 'SITE_UPGRADE'

/**
 * Defines config per behavior.
 */
export type GfppWithBehavior<
  T extends GfppBehavior,
  K extends ActionPermissions = ActionPermissions,
> = {
  behavior: T
} & K

/**
 * Basic GFPP definition, usually used for icon buttons and mobile main actions.
 * Supported behaviors: `DEFAULT`, `HIDE`, `CUSTOM_ACTION` (optional)
 */
export type GfppBasicButton<T extends ActionDefinition = never> =
  | (T extends ActionDefinition ? GfppWithBehavior<'CUSTOM_ACTION', T> : {})
  | GfppWithBehavior<'DEFAULT'>
  | GfppWithBehavior<'HIDE'>

/**
 * The most powerful GFPP button definition, used to define almost any GFPP behavior that the platform supports.
 * Supported behaviors: `CUSTOM_ACTION`, `DEFAULT`, `DEFAULT1`, `DEFAULT2`, `HIDE`.
 */
export type GfppAdvancedButton =
  | GfppWithBehavior<'CUSTOM_ACTION', MainActionDefinition>
  | GfppWithBehavior<'DEFAULT'>
  | GfppWithBehavior<'DEFAULT1'>
  | GfppWithBehavior<'DEFAULT2'>
  | GfppWithBehavior<'HIDE'>

/**
 * GFPP definition for the upgrade button.
 * Supported behaviors: `DEFAULT`, `HIDE`, `SITE_UPGRADE`, 'CUSTOM_ACTION'.
 */
export type GfppUpgradeButton<T extends ActionDefinition = never> =
  | GfppBasicButton<T>
  | GfppWithBehavior<'SITE_UPGRADE'>

/**
 * Defines a pages group to be displayed in the pages panel.
 */
export interface PagesGroup {
  /**
   * The title of the pages group.
   */
  title: string
  /**
   * Array of page IDs to include in the group, provided by the application.
   */
  pages: string[]
  /**
   * The ID of the pages group. This ID is sent to BI and as eventPayload when one of the actions is clicked.
   */
  id: string
  /**
   * Array of group actions to include in the group context menu, provided by the application.
   */
  groupActions?: PagesGroupAction[]
}

/**
 * Defines the options for sorting innerRoutes. All of the options are from [siteMaps](../../articles/glossary.md#sitemap) definitions.
 */
export const enum InnerRoutesSortOptions {
  /**
   * The title of the page.
   */
  TITLE = 'title',
  /**
   * No sort is applied.
   */
  NO_SORT = 'no-sort',
}

/**
 * InnerRoutesSortOptions represented as strings union
 */
export type InnerRoutesSortOptionsStrings = 'title' | 'no-sort'

/**
 * TPAPageId is a unique ID that represents all the instances of an app's page type.
 */
export type TPAPageId = string

/**
 * PagePreset defines the metadata of a page variant.
 */
export type PageVariant = {
  /**
   * The ID of the variant.
   */
  id: string
  /**
   * The name of the variant.
   */
  name: string
  /**
   * The description of the variant.
   */
  description: string
  /**
   * The image source. Must be a full URL.
   */
  src: string
}

/**
 * `CustomSubtitleContent` - options for configuration of the subtitle.
 */
export type CustomSubtitleContent = {
  /**
   * The subtitle text.
   */
  text: string
  /**
   * Optional. URL of the help article that provides instructions for site developers on how to use variant pages.
   */
  learnMoreUrl?: string
}

/**
 * `VariantsPanelOptions` - options for variants panel configuration.
 */
export type VariantsPanelOptions = {
  /**
   * Optional. Whether variants that have already been added are hidden in the variant's modal. Default is `true`. [Learn more about page variants](../../articles/app-reflow/reflow-variants.md)
   */
  hideAddedVariants?: boolean
  /**
   * optional. custom subtitle for the variants panel, which includes text and an optional learn more link.
   */
  customSubtitle?: CustomSubtitleContent
  /**
   * optional. Whether to show the footer of the variants panel. Default is true.
   */
  showFooter?: boolean
}

/**
 * `variantsData` - An object that determines whether users can replace the page with a variant page.
 */
export type VariantsData = {
  /**
   * Array of page variants that will be displayed in the page variants dialog.
   */
  variants: PageVariant[]
  /**
   * Optional. An object with configuration of the variants panel.
   */
  variantsPanelOptions?: VariantsPanelOptions
}

/**
 * PageReplaceOptions represents the options of the page replacement under the TPAPageId.
 */
export type PageReplaceOptions = {
  /**
   * Optional. A boolean value that determines whether users can replace the page with a custom page. Default is `true`. [Learn more about replaceable pages](../../articles/app-reflow/reflow-replace.md)
   */
  replaceable?: boolean
  /**
   * Optional. When using lazy migration, the new slug of the page under the router prefix (for example, the router prefix is <code>pricing-plans</code>, the new slug is <code>plans</code>, the URL will be <code>pricing-plans/plans</code>).
   */
  slug?: string
  /**
   * Optional. URL of the help article in Wix Docs that provides instructions for site developers on how to build the specific app page replacer.
   */
  replacerHelpUrl?: string
  /**
   * Optional. An object that determines whether users can replace the page with a variant page. [Learn more about page variants](../../articles/app-reflow/reflow-variants.md)
   * */
  variantsData?: VariantsData
  /**
   * Optional. Name of the premium feature for quota validation.
   * */
  premiumFeatureName?: string
}

/**
 * PageReplace is an object the key is tpaPageId and the value is the page replacement options.
 */
export type PageReplace = Record<TPAPageId, PageReplaceOptions>

export type PageSettings = {
  /**
   * The title for the tab in the pages panel.
   */
  title: string
  /**
   * The help ID for the topic that opens when the user clicks `?`.
   */
  helpId?: string
  /**
   * The tab IFrame URL.
   */
  url?: string
  /**
   * The semantic type of the setting tab.
   */
  type: PageSettingsTabTypeStrings
} & ActionPermissions

export type ApplicationAction =
  | SimpleApplicationAction
  | AddPageApplicationAction

export type SimpleApplicationAction = {
  /**
   * The title of the action.
   */
  title: string
  /**
   * The icon of the action.
   */
  icon: string
  /**
   * The event type sent for the action.
   */
  event: string
} & ActionPermissions

export type AddPageApplicationAction = {
  /**
   * The semantic type of action, to indicate the action's meaning to the Editor.
   */
  type: ApplicationActionsTypeStrings
  /**
   * Optional content for a tooltip over the **Add Page** button (when the action's type is set to `add_page`).
   */
  tooltip?: string
} & SimpleApplicationAction

export type ApplicationSettings = {
  /**
   * Specifies the label to use to define the pages group in the pages panel or site structure.
   */
  displayName: string
  /**
   * Specifies the ID of the help article to open in the pages view.
   */
  helpId?: string
  /**
   * Specifies the grouping method for application pages in the pages panel.
   */
  grouping?: /**
   * No grouping, display flat pages structure
   */
  | {groupBy: 'none'}
    /**
     * Group by routers. Pages belonging to routers will be grouped together under the title of the router.
     */
    | {groupBy: 'routers'}
    /**
     * Custom group. The app will specify the group's content.
     */
    | {
        groupBy: 'custom'
        customGroups: PagesGroup[]
      }
  /**
   * Defines the page replace map declaration, that map each tpaPageId to the replacement options you can define.
   */
  pageReplace?: PageReplace
}

/**
 * Describes the appearance of application pages and the editing experience in the Editor's Pages Panel. Learn more in [this article](../../articles/pages-manifest.md).
 */
export interface PagesManifest {
  /**
   * Specifies the actions that appear on the page's context menu in the pages panel.
   * The Editor presents page actions according to the page state.
   * If the string 'Pages_Actions_Page_Rename' is given instead of the page action object, the Editor adds a rename action handled by the Editor.
   */
  pageActions?: {
    /**
     * Default actions - when the page has no state.
     */
    default: SimplePageAction[] | PageActionsWithEditorStateOverrides
    /**
     * The actions to use when the page has a special state, which is set by [EditorSDK.document.pages.setState](../../document/pages.md#setstate).
     */
    [state: string]: SimplePageAction[] | PageActionsWithEditorStateOverrides
  }
  /**
   * Specifies the tabs and context menu items that appear for an application page in the pages panel.
   * The Editor presents pages settings according to the page state.
   */
  pageSettings?: {
    /**
     * Default settings - when page has no state.
     */
    default: PageSettings[] | PageSettingsWithEditorStateOverrides
    /**
     * The settings to use when the page has a special state, which is set by [EditorSDK.document.pages.setState](../../document/pages.md#setstate).
     */
    [state: string]: PageSettings[] | PageSettingsWithEditorStateOverrides
  }
  /**
   * Specifies the actions displayed for the application itself in the context menu, in the pages panel tabs.
   */
  applicationActions?: {
    default: ApplicationAction[]
  }
  applicationSettings?: {
    default: ApplicationSettings
  }

  /**
   * Specifies the page descriptors.
   * The Editor presents page descriptors according to the page state.
   */
  pageDescriptors?: {
    /**
     * Default descriptors - when page has no state.
     */
    default: SimplePageDescriptors | PageDescriptorsWithEditorStateOverrides
    /**
     * The descriptors to use then the page has a special state (set by the EditorSDK.document.pages.setState).
     */
    [state: string]:
      | SimplePageDescriptors
      | PageDescriptorsWithEditorStateOverrides
  }
  /**
   * Defines the options for sorting innerRoutes. All of the options are from [siteMaps](../../articles/glossary.md#sitemap) definitions.
   */
  sortInnerRoutes?: InnerRoutesSortOptionsStrings
}

/**
 * Define the Editor conditions.
 */
export interface EditorConditions {
  /**
   * Editor with Dev mode open.
   */
  advanced?: boolean
  /**
   * Editor in mobile mode.
   */
  mobile?: boolean
}

/**
 * Page action definition.
 */
export interface SimplePageAction extends ActionPermissions {
  /**
   * Semantic type of action to indicate the action's meaning to the Editor.
   * If specified, then other fields may be omitted, because the Editor knows how to handle the event and which icon and title to use.
   */
  type?: PageActionsTypeStrings
  /**
   * The eventType sent to the app.
   * If the action is one of the `PageActionsType` enum, the Editor knows how to perform the action, and the application can omit the `event` field.
   */
  event?: string
  /**
   * The text to appear for the action in the context menu in the pages tree.
   * If the action is one of the `PageActionsType` enum, the Editor knows which title to put and the application can omit the `title` field.
   */
  title?: string
  /**
   * The icon to appear for the action in the context menu in the pages tree.
   * If the action is one of the `PageActionsType` enum, the Editor knows which title to put and the application can omit the `icon` field.
   */
  icon?: string
  /**
   * The actionName that will be reported to BI. This string should be untranslated so that it is consistent for all reported BI events.
   *
   * @deprecated The eventType will be reported to BI, and this is never translated. Therefore it is unnecessary to provide an actionName solely for BI purposes.
   */
  actionName?: string
  /**
   * Optional. Name of the premium feature for quota validation.
   * */
  premiumFeatureName?: string
}

export type PageActionsOverride = {
  /**
   * Condition to apply to these page actions.
   */
  condition: EditorConditions
  /**
   * The page actions to use when the condition is met.
   */
  override: SimplePageAction[]
}

/**
 * Format to describe the page actions with Editor state overrides.
 */
export interface PageActionsWithEditorStateOverrides {
  /**
   * Page actions to use when no override is applied.
   */
  defaultValues: SimplePageAction[]
  /**
   * Overrides the default page actions if conditions are met.
   * You can add more then one override with different conditions.
   * The last matching condition is applied.
   */
  overrides: PageActionsOverride[]
}

export type PageSettingsOverride = {
  /**
   * Condition to apply to these page actions.
   */
  condition: EditorConditions
  /**
   * The page actions to use when the condition is met.
   */
  override: PageSettings[]
}

export interface PageSettingsWithEditorStateOverrides {
  /**
   * Page actions to use when no override is applied.
   */
  defaultValues: PageSettings[]
  /**
   * Overrides the default page settings if conditions are met.
   * You can add more then one override with different conditions.
   * The last matching condition is applied.
   */
  overrides: PageSettingsOverride[]
}

/**
 * Simple format to describe page descriptors.
 */
export interface SimplePageDescriptors {
  /**
   * Specifies the icon shown in the pages view.
   */
  icon?: string
  /**
   * Specifies the tooltip shown in the pages view.
   */
  tooltip?: string
  /**
   * The order index of the page in pages view.
   * The index needs to be positive and greater than 0. The default for unordered pages is 0.
   * Higher index pages come first, those with no value or zero come last.
   * Pages with the same index appear in the order of the pages in document services.
   */
  orderIndex?: number
}

export type PageDescriptorsOverride = {
  /**
   * Condition to apply this descriptor.
   */
  condition: EditorConditions
  /**
   * The descriptor to use in case the condition is met.
   */
  override: SimplePageDescriptors
}

/**
 * Format to describe the page descriptors with overrides.
 */
export interface PageDescriptorsWithEditorStateOverrides {
  /**
   * Descriptor to use when no override applied.
   */
  defaultValues: SimplePageDescriptors
  /**
   * Overrides the default descriptor if conditions are met.
   * You can add more than one override with different conditions.
   * The last matching condition is applied.
   */
  overrides: PageDescriptorsOverride[]
}

/**
 * @description - Describes the application router's pages' appearance and editing experience in the Editor Pages Panel. Learn more in [this article](../../articles/routers-manifest.md).
 * @deprecated - use PagesManifest instead.
 */
export interface RoutersManifest {
  /**
   * DEPRECATED
   * Specifies settings for each pagesGroup, indexed by the routerConfig group.
   */
  pagesGroups?: {
    [index: string]: {
      /**
       * The label displayed in the pagesPanel.
       */
      label?: string
    }
  }
  /**
   * Specifies the actions that appear in the context menu of the router page in the left pages tree and pages panel.
   * If string 'Pages_Actions_Page_Rename' is given instead of the page action object, the Editor renames the action handled by the Editor.
   */
  pageActions?: {
    default: {
      [index: number]: {
        /**
         * The eventType sent to the app.
         */
        event: string
        /**
         * The text displayed for the action in the context menu in the pages tree.
         */
        title?: string
        /**
         * The icon displayed for the action in the context menu in the pages tree.
         */
        icon?: string
        /**
         * Semantic type of action to give a clue to the Editor as to the action's meaning.
         */
        type?: string
      }
    }
  }
  /**
   * Specifies the tabs in the Settings panel displayed for a router page in the page settings panel.
   */
  pageSettings?: {
    default: {
      [index: number]: {
        /**
         * The title for the tab in the pages panel.
         */
        title: string
        /**
         * The help ID that opens when the user chooses `?`
         */
        helpId: string
        /**
         * The tab IFrame URL.
         */
        url: string
        /**
         * Semantic type of action to give a clue to the Editor as to the action's meaning.
         */
        type: PageSettingsTabTypeStrings
      }
    }
  }
  /**
   * Specifies the action displayed for the router itself in the context menu and tree.
   * If the `mainAction` is not specified in `routerViewActions`, the first action of *add_page* type is used instead.
   */
  routerActions?: {
    default: {
      [index: number]: {
        /**
         * The title of the action.
         */
        title: string
        /**
         * The icon of the action.
         */
        icon: string
        /**
         * The event type sent for the action.
         */
        event: string
        /**
         * DEPRECATED: Specifies whether the router actions should be shown outside of developer mode.
         */
        showOnAllModes?: boolean
        /**
         * Semantic type of action to give a clue to the Editor as to the action's meaning.
         */
        type?: ApplicationActionsTypeStrings
      }
    }
  }
  /**
   * Specifies main and secondary actions displayed in the pages panel.
   */
  routerViewActions?: {
    default: {
      mainAction?: {
        /**
         * The event type sent for the action.
         */
        event: string
        /**
         * The title of the action.
         */
        title: string
        /**
         * Category value for BI event that is sent on click.
         */
        biActionName?: string
      }
      secondaryActions?: Array<{
        /**
         * The event type sent for the action.
         */
        event: string
        /**
         * The icon of the action.
         */
        icon: string
        /**
         * The tooltip of the action.
         */
        tooltip: string
        /**
         * Category value for the BI event that is sent on click.
         */
        biActionName?: string
      }>
    }
  }
}

export enum PageSettingsTabType {
  /**
   * Settings tab marked by *layout* type but with no URL field show the default Editor layout tab.
   */
  LAYOUT = 'layout',
  /**
   * Settings tab marked by *permissions* type but with no URL field show the default Editor permissions tab
   */
  PERMISSIONS = 'permissions',
  /**
   * Settings tab marked by *page_info* type are opened from the *Settings* link in the page actions context menu.
   */
  PAGE_INFO = 'page_info',
  /**
   * Settings tab marked by *seo* type are opened from the *SEO* link in the page actions context menu.
   */
  SEO = 'seo',
  /**
   * Settings tab marked by *a11y* type are opened from the *Accessibility* link in the page actions context menu.
   * Note: currently availabe only for Editor3
   */
  A11Y = 'a11y',
  /**
   * Settings tab marked by *social_share* type are opened from the *Social Share* link in the page actions context menu.
   * Note: currently availabe only for Editor3
   */
  SOCIAL_SHARE = 'social_share',
}

/**
 * PageSettingsTabType represented as strings union
 */
export type PageSettingsTabTypeStrings =
  | 'layout'
  | 'permissions'
  | 'page_info'
  | 'seo'
  | 'a11y'
  | 'social_share'

export enum ApplicationActionsType {
  /**
   * An action with this type is treated by the editor in a special way - the Editor connects it to a button in the footer of the Pages panel tab view.
   * The same title is used for the button and the action.
   * There can only be one like this.
   */
  ADD_PAGE = 'add_page',
}

/**
 * ApplicationActionsType represented as strings union
 */
export type ApplicationActionsTypeStrings = 'add_page'

export enum PageActionsType {
  /**
   * An action of this type, if specified without *event*, performs page rename.
   * If specified with *event*, the Editor notifies the application about the event, and does not perform page rename.
   */
  RENAME = 'page_rename',
  /**
   * An action of this type, if specified without *event*, performs page removal.
   * If specified with *event*, the Editor notifies the application about the event, and does not perform page removal.
   */
  DELETE = 'page_delete',
  /**
   * An action of this type, if specified without *event*, performs setting as homepage.
   * If specified with *event*, the Editor notifies the application about the event, and does not perform setting as homepage.
   * Note that the default event is implemented and available only for pages within the main pages panel.
   */
  SET_AS_HOMEPAGE = 'page_set_as_homepage',
  /**
   * An action of this type, if specified without *event*, performs page duplicate.
   * If specified with *event*, the Editor notifies the application about the event, and does not perform duplicate.
   * Note that the default event is implemented and available only for pages within the main pages panel
   * AND only if the page is duplicatable (according to the Editor's validation rules).
   */
  DUPLICATE = 'page_duplicate',
  /**
   * An action of this type, if specified without *event*, performs page copy.
   * If specified with *event*, the Editor notifies the application about the event, and does not perform copy.
   * Note that the default event is implemented and available only for pages within the main pages panel
   * AND only if the page is copyable (according to the Editor's validation rules).
   */
  COPY = 'page_copy',
}

/**
 * PageActionsType represented as strings union
 */
export type PageActionsTypeStrings =
  | 'page_rename'
  | 'page_delete'
  | 'page_set_as_homepage'
  | 'page_duplicate'
  | 'page_copy'

/**
 * Describes a main action configuration in the App Manager panel. Learn more in [this article](../../articles/app-descriptor-manifest.md).
 */
export interface MainActionConfig extends ActionPermissions {
  /**
   * The action's display name.
   */
  title: string
  /**
   * Unique ID of the action to pass to the app in the payload of the *appActionClicked* platform event.
   */
  actionId: string
  /**
   * Accepts an svg name for editorSymbols.
   */
  icon: string
}

/**
 * Describes a base action configuration in the App Manager panel. Learn more in [this article](../../articles/app-descriptor-manifest.md).
 */
export interface BaseActionConfig extends ActionPermissions {
  /**
   * The action's display name.
   */
  title: string
  /**
   * Accepts an svg name for editorSymbols.
   */
  icon: string
  /**
   * Either editorAction | dashboard, which sets the grouping needed for Editor X - not relevant for Classic Editor.
   */
  type: 'editorActions' | 'dashboard'
}

/**
 * Describes a custom action in the App Manager panel. Learn more in [this article](../../articles/app-descriptor-manifest.md).
 */
export interface CustomActionConfig extends BaseActionConfig {
  /**
   * Unique ID of the action to pass to the app in the payload of the *appActionClicked* platform event.
   */
  actionId: string
}

/**
 * Describes an external link action in the App Manager panel.
 */
export interface ExternalLinkActionConfig extends BaseActionConfig {
  /**
   * The external URL for the site. The link opens in a new window.
   */
  url: string
}

/**
 * Describes the app actions, pages and editor setup in My Business panel. Learn more in [this article](../../articles/app-descriptor-manifest.md).
 */
export interface AppDescriptor {
  /**
   * Application-specific configuration for My Business panel.
   */
  application?: {
    /**
     * The name of the application.
     */
    name: string
    /**
     * Configuration for application pages.
     */
    pages: {
      /**
       * Array of image URLs to display for the application. Only the first 2 images are used.
       */
      images: string[]
    }
    /**
     * Configuration for the editor setup.
     */
    editorSetup: {
      /**
       * Title for the editor setup.
       */
      title: string
      /**
       * Description text for the editor setup.
       */
      description: string
    }
  }
  mainActions: MainActionConfig[]
  customActions: (CustomActionConfig | ExternalLinkActionConfig)[]
  defaultActions: {
    /**
     * If passed then Upgrade is shown, otherwise, it's hidden.
     * Clicking Upgrade opens the URL of the Package Picker or Site Upgrade depending on the passed value.
     */
    upgrade?: {
      /**
       * app upgrade or site upgrade package picker.
       */
      upgradeType: UpgradeTypes
      /**
       * the upgrade description text - for example: "to get advanced features,".
       */
      upgradeText: string
      /**
       * the upgrade link's text - for example: "upgrade Ascend.".
       */
      upgradeLinkText: string
    }
    /**
     * If passed then Learn More is shown, otherwise, it's hidden.
     * Clicking it opens the help center with the ID of the matching article.
     */
    learnMoreKB?: string
    /**
     * Always shown, unless passed with 'HIDE'.
     */
    uninstall?: 'HIDE'
  }
}

/**
 * Pages Group's Action definition.
 */
export interface PagesGroupAction extends ActionPermissions {
  /**
   * The eventType sent to the app. eventPayload will include the groupTitle.
   * The event will be reported to BI. This string should be untranslated so that it is consistent for all reported BI events.
   */
  event: string
  /**
   * The text to appear for the action in the context menu in the pages tree.
   */
  title: string
  /**
   * The icon to appear for the action in the context menu in the pages tree.
   */
  icon: string
}

/**
 * The permissions for each action, which determines whether the action appears.
 */
export interface ActionPermissions {
  permissions?: {
    /**
     * Defines the custom permissions for the action.
     */
    custom?: string[]
    /**
     * Defines the group permissions for the action.
     */
    groups?: GroupPermissions[]
  }
}

/**
 * Defines the group permissions options.
 */
export type GroupPermissions =
  | 'EDIT_CONTENT'
  | 'LAYOUT_AND_DESIGN'
  | 'EDIT_STRUCTURE'

export interface WidgetPresetsManifest {
  /**
   * Help ID for the presets help article.
   */
  helpId?: string
  /**
   * The presets for your application.
   */
  presets: WidgetPreset[]
  /**
   * List of categories to which presets can be assigned.
   * At least 2 categories are required for the category navigation to be displayed.
   */
  categories?: WidgetPresetsCategory[]
  /**
   * Determines whether to show the entry point in the presets panel that opens the design panel (defaults to `true`).
   * The entry point won't be shown if the Design GFPP button is hidden.
   */
  isDesignButtonVisible?: boolean
  /**
   * Determines whether to show the entry point in the presets panel that opens the layout panel (defaults to `true`).
   * The entry point won't be shown if the Layout GFPP button is hidden.
   */
  isLayoutButtonVisible?: boolean
}

export interface WidgetPreset {
  /**
   * The ID of the preset.
   */
  id: string
  /**
   * The ID of the category to which this preset belongs.
   */
  categoryId?: string
  /**
   * The name of the preset.
   */
  name?: string
  /**
   * The image source. Must be a full URL.
   */
  src: string
  /**
   * The image source for hover state. Must be a full URL.
   */
  hoverSrc?: string
}

export interface WidgetPresetsCategory {
  /**
   * The ID of the category.
   */
  id: string
  /**
   * The name of the category.
   */
  name: string
}

export type WidgetDesignPresets =
  | WidgetDesignPreset[]
  | WidgetDesignPresetsObject

export interface WidgetDesignManifest {
  /**
   * Here you define the label for the tab, the component grouping and the appearance of the edit panel.
   */
  tabs: WidgetDesignTab[]
  /**
   * Here you define the presets for your application.
   */
  presets?: WidgetDesignPresets
  /**
   * The title that appears at the top of the widget design panel.
   */
  title: string
  /**
   * The title that appears at the top of the presets panel.
   */
  presetsTitle?: string
  /**
   * Help ID for the widget design customization help article.
   */
  customHelpId?: string
  /**
   * Help ID for the presets help article.
   */
  presetHelpId?: string
}

export interface WidgetDesignStyleParamOverrides {
  [compType: string]: WidgetDesignStyleParamOverride
}

export interface WidgetDesignStyleParamOverride {
  /**
   * The mapping of style parameters in the design panel to style parameters in the widget components' skins.
   *
   * @example {'txtlbl': ['txt2','txt']}
   */
  [styleParameter: string]: string[]
}

export interface tooltipLinkHelp {
  text: string
  helpId: string
}

export interface tooltipLinkDashboard {
  text: string
  url: string
}

export interface WidgetDesignTab {
  /**
   * The tab label. Label translations should be provided by the application.
   */
  label: string

  /**
   * A text to be displayed in the tooltip.
   */
  tooltip?: string

  /**
   * Append a link to the end of the tooltip, linking either to the Help Center or to the Dashboard.
   * The link is displayed only if a tooltip is provided.
   * `text` sets the text of the link,
   * and is displayed only if `helpId` or `url` is provided. Link text translation should be provided
   * by the application.
   * `helpId` is the ID of the Help Center article.
   * `url` is the relative URL in the dashboard.
   */
  tooltipData?: tooltipLinkHelp | tooltipLinkDashboard

  /**
   * An object that defines which components are considered part of this editing group.
   * You can group components by compType (component type), role, or a combination of both.
   *
   * @example {compTypes: [‘wixui.TimePicker’], roles: [‘previous_button’]}
   */
  groups: WidgetDesignGroups
  /**
   * Contains the definition for the appearance of the design panel. Sections can be sectioned by role or by design parameter.
   * Role-based sections enable you to edit the design parameters of components with different roles separately.
   *
   * @example {roles: [‘previous_button’]}
   */
  sections?: WidgetDesignSection[]
  /**
   * Enables you to display the tab only if specific roles exist in the widget. If any of the roles included in the array do not exist in the widget, the tab is hidden.
   *
   * @example {dependents: ['submitButtonRole', 'nextButtonRole']}
   */
  dependents?: string[]
  /**
   * Enables you to define a mapping of style parameter overrides for each component type.
   * Use this field when you need to jointly edit components that are not defined by the same set of style parameters.
   * The mapping tells the editor which style parameters to modify when the user selects a value in the design panel.
   *
   * @example
      {
        'styleParamOverrides': {
          'wixui.RatingsInput': {
            'txtlbl': [
              'labelFontColor',
              'filledShapeColor',
              'shapeBorderColor'
            ],
            'fntlbl': [
              'labelFont'
            ]
          }
        }
      }
   */
  styleParamOverrides?: WidgetDesignStyleParamOverrides
}

// reexport WidgetDesignTab as WidgetDesignTabs for backward compatibility
export interface WidgetDesignTabs extends WidgetDesignTab {}

export interface WidgetDesignPresetsObject {
  [theme: string]: Omit<WidgetDesignPreset, 'id'>
}

export interface WidgetDesignPreset {
  /**
   * The id of the preset.
   */
  id: string
  /**
   * The image source.
   */
  src: string
  /**
   * The dimensions of the preset image. Should be defined according to width and height (px).
   */
  layout: {
    width: string
    height: string
  }
}

export interface WidgetDesignGroups {
  compTypes?: string[]
  roles?: string[]
}

export interface WidgetDesignSection {
  /**
   * Here you should declare the state of the component for which the design should apply. E.g. hover, regular, disabled.
   */
  state: WidgetDesignStates
  /**
   * Here you should declare the category of the design panel which this section controls i.e. fill, text, corners.
   */
  category: WidgetDesignCategories
  /**
   * Here you should declare the label for the section.
   */
  label: string
  /**
   * Here you should declare the priority of the section. A lower number indicates a higher priority meaning this section will appear higher up on the panel.
   */
  priority: number
  /**
   * If the section should only change the design of components in the group with a certain role, you should declare here the roles whose design is controlled by this section here. E.g. you might have two separate sections to design your 'previous' and your 'next' buttons.
   */
  roles?: string[]
  /**
   * If the section should only change the design of a certain style parameter, e.g. just the background color of the components in the group, you should declare the style parameter here.
   * Use [`styleParamOverrides`](./WidgetDesignTabs.md#styleparamoverrides) if the group contains different components with different names for the same style parameter (for example, the placeholder text style parameter may be defined as 'txt2', 'txt_placeholder' or 'inputPlaceholderTextColor').
   */
  styleParam?: string
  /**
   * Optional. You can also define subsections for the panel, should you wish to have more granular control over which design parameters are edited in the panel. I.e. if you want to design the title color of an input field separately the color for the required indicator.
   */
  subsections?: WidgetDesignSubSection[]
  /**
   * Optional. Defines which states should be edited. Can only be used when the widget design panel is sectioned by role. The `role` property is defined in `WidgetDesignSection`.
   */
  states?: WidgetDesignStates[]
}

// reexport WidgetDesignSection as WidgetDesignSections for backward compatibility
export interface WidgetDesignSections extends WidgetDesignSection {}

export interface WidgetDesignSubSection {
  /**
   * Here you should declare the label for the sub-section.
   */
  label: string
  /**
   * The style parameter which is controlled by this subsection.
   */
  styleParam: string
  /**
   * Here you should declare the priority of the sub section. The priority is a negative number, where a lower number indicates a higher priority. I.e. -2 has a higher priority than -1.
   */
  priority?: number
}

export type WidgetDesignStates =
  | 'regular'
  | 'hover'
  | 'disabled'
  | 'active'
  | 'focus'
  | 'open'
  | 'closed'
  | 'error'
  | 'selected'
  | 'scrolled'
  | 'hidden'
export type WidgetDesignCategories =
  | 'text'
  | 'corners'
  | 'shadow'
  | 'fill'
  | 'border'
  | 'other'
